import { useState, useEffect, useCallback } from 'react';
import { collection, addDoc, query, orderBy, limit, onSnapshot, serverTimestamp, doc, updateDoc, where, getDoc, getDocs } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useAuth } from '../context/AuthContext';

export function useConversations() {
  const { user } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    const q = query(collection(db, 'conversations'), where('members', 'array-contains', user.uid), orderBy('lastMessageAt', 'desc'));
    const unsub = onSnapshot(q, (snap) => {
      setConversations(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });
    return unsub;
  }, [user]);

  async function createConversation(otherUserId, otherUserName) {
    if (!user) return null;
    const q = query(collection(db, 'conversations'), where('members', 'array-contains', user.uid));
    const snap = await getDocs(q);
    const existing = snap.docs.find(d => d.data().members.includes(otherUserId));
    if (existing) return existing.id;
    const ref = await addDoc(collection(db, 'conversations'), {
      members: [user.uid, otherUserId],
      memberNames: { [user.uid]: user.displayName, [otherUserId]: otherUserName },
      lastMessage: '', lastMessageAt: serverTimestamp(), createdAt: serverTimestamp(), type: 'direct'
    });
    return ref.id;
  }

  return { conversations, loading, createConversation };
}

export function useMessages(conversationId) {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!conversationId) return;
    const q = query(collection(db, 'conversations', conversationId, 'messages'), orderBy('createdAt', 'asc'), limit(100));
    const unsub = onSnapshot(q, (snap) => {
      setMessages(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });
    return unsub;
  }, [conversationId]);

  const sendMessage = useCallback(async (text) => {
    if (!user || !conversationId || !text.trim()) return;
    await addDoc(collection(db, 'conversations', conversationId, 'messages'), {
      text: text.trim(), senderId: user.uid, senderName: user.displayName,
      senderPhoto: user.photoURL || null, createdAt: serverTimestamp(), type: 'text'
    });
    await updateDoc(doc(db, 'conversations', conversationId), {
      lastMessage: text.trim(), lastMessageAt: serverTimestamp()
    });
  }, [user, conversationId]);

  return { messages, loading, sendMessage };
}
